import { cn } from '@/lib/utils';

interface SkeletonProps {
    className?: string;
}

/**
 * Base Skeleton Component
 *
 * A pulsing placeholder for loading content.
 */
export const Skeleton = ({ className }: SkeletonProps) => (
    <div
        className={cn('animate-pulse bg-muted rounded', className)}
        aria-hidden="true"
    />
);

/**
 * Text line skeleton
 */
export const SkeletonText = ({ className }: SkeletonProps) => (
    <Skeleton className={cn('h-4 w-full', className)} />
);

/**
 * Avatar/circular skeleton
 */
export const SkeletonCircle = ({ className }: SkeletonProps) => (
    <Skeleton className={cn('h-10 w-10 rounded-full', className)} />
);

/**
 * Card skeleton for product cards, etc.
 */
export const SkeletonCard = ({ className }: SkeletonProps) => (
    <div className={cn('rounded-lg border border-border overflow-hidden', className)}>
        <Skeleton className="h-48 w-full rounded-none" />
        <div className="p-4 space-y-3">
            <Skeleton className="h-4 w-3/4" />
            <Skeleton className="h-4 w-1/2" />
            <Skeleton className="h-8 w-full mt-4" />
        </div>
    </div>
);

/**
 * Product grid skeleton
 */
interface SkeletonGridProps {
    count?: number;
    className?: string;
}

export const SkeletonProductGrid = ({ count = 6, className }: SkeletonGridProps) => (
    <div
        className={cn('grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6', className)}
        role="status"
        aria-label="Loading products"
    >
        {Array.from({ length: count }).map((_, index) => (
            <SkeletonCard key={index} />
        ))}
        <span className="sr-only">Loading products...</span>
    </div>
);

/**
 * List item skeleton
 */
export const SkeletonListItem = ({ className }: SkeletonProps) => (
    <div className={cn('flex items-center space-x-4 p-4', className)}>
        <SkeletonCircle />
        <div className="flex-1 space-y-2">
            <Skeleton className="h-4 w-1/4" />
            <Skeleton className="h-4 w-1/2" />
        </div>
    </div>
);

/**
 * Table row skeleton
 */
interface SkeletonTableProps {
    rows?: number;
    columns?: number;
    className?: string;
}

export const SkeletonTable = ({ rows = 5, columns = 4, className }: SkeletonTableProps) => (
    <div className={cn('border border-border rounded-lg overflow-hidden', className)}>
        {/* Header */}
        <div className="bg-muted border-b border-border p-3 grid gap-4" style={{ gridTemplateColumns: `repeat(${columns}, 1fr)` }}>
            {Array.from({ length: columns }).map((_, i) => (
                <Skeleton key={i} className="h-4" />
            ))}
        </div>
        {/* Rows */}
        {Array.from({ length: rows }).map((_, rowIndex) => (
            <div
                key={rowIndex}
                className="p-3 border-b last:border-b-0 border-border grid gap-4"
                style={{ gridTemplateColumns: `repeat(${columns}, 1fr)` }}
            >
                {Array.from({ length: columns }).map((_, colIndex) => (
                    <Skeleton key={colIndex} className="h-4" />
                ))}
            </div>
        ))}
    </div>
);

export default Skeleton;
